import { api } from '../../core/network/api';
import type { AuthUser } from '../../core/state/auth-store';

/**
 * Thin REST wrapper for the Settings screen. Only one live read is needed:
 * GET /auth/me (backend/src/core/auth/auth.controller.ts) refreshes the
 * account info the screen displays, mirroring the `{ user }` envelope every
 * other auth response already uses (see core/auth/auth-api.ts's
 * AuthResponse). Logout goes straight through the existing
 * core/auth/auth-api.ts `logout()` wrapper from the screen — no wrapper is
 * added here for it.
 */

interface MeResponse {
  user: AuthUser;
}

/** GET /auth/me — the caller's own current account info. */
export async function fetchMe(): Promise<AuthUser> {
  const res = await api.get<MeResponse>('/auth/me');
  return res.user;
}

export interface AccountView {
  id: string;
  email: string;
  username: string | null;
  emailVerified: boolean;
  kycStatus: string;
  level: number;
  xp: number;
  createdAt: string;
}

/** GET /me/account — the signed-in player's own account record. */
export function fetchAccount(): Promise<AccountView> {
  return api.get<AccountView>('/me/account');
}

/** PATCH /me/account — rename. Rejects with 409 when the name is taken. */
export function updateAccount(username: string): Promise<AccountView> {
  return api.patch<AccountView>('/me/account', { username });
}

/** POST /me/account/password — verifies the current password and revokes every refresh token. */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return api.post<void>('/me/account/password', { currentPassword, newPassword });
}

/**
 * POST /me/account/close — closes the account.
 *
 * Returns the mode the server took: `deleted` when nothing financial was
 * attached, `anonymised` when records had to be kept. Rejects with 409 while a
 * withdrawal is still pending.
 */
export function deleteAccount(password: string): Promise<{ mode: string }> {
  return api.post<{ mode: string }>('/me/account/close', { password });
}
